import React, { useEffect, useRef, useState } from 'react'
import {
    COLOR_NAME,
    DELIVERY_TIME,
    ORDER_METHOD,
    ORDER_METHOD_MAP,
    PARAMS_NAME,
} from '../constants'
import useOrderStatus from '../hooks/useOrderStatus'
import audioBeep from '../assets/audio/beep.wav'
import ModalForm from '../components/common/modal-form'
import DynamicColor from '../components/common/dynamic-color'
import StatusList from '../components/common/StatusList'
import ListFoodOrdered from './ListFoodOrdered'

import activeSuccess from '../assets/icons/success.gif'
import inactiveSuccess from '../assets/icons/success_static.jpg'
import activeAccept from '../assets/icons/accept.gif'
import inactiveAccept from '../assets/icons/accept_static.jpg'
import activeProcessing from '../assets/icons/processing.jpg'
import inactiveProcessing from '../assets/icons/processing_static.png'
import activeShipping from '../assets/icons/shipping.gif'
import inactiveShipping from '../assets/icons/shipping_static.jpg'
import failedIcon from '../assets/icons/broken_heart.gif'

import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getMinTimePrepare, getRestaurantSettings } from '../selectors'
import useDidMountEffect from '../hooks/useDidMountEffect'
import {
    convertServerTime,
    formatTimeFromMillisecond,
    calcPriceSale,
} from '../utils'
import { useLocation } from 'react-router'
import paths from '../routes/paths'

const ModalWebStatus = () => {
    const audioRef = useRef()
    const refTimeout = useRef()
    const [statusInfo, orderInfo] = useOrderStatus(
        PARAMS_NAME.ORDER_WEB_ID,
        audioRef
    )
    const [currentStatus, setCurrentStatus] = useState(-3)
    const [countDownTime, setCountDownTime] = useState(-1)
    const { t } = useTranslation()
    const minTimePrepare = useSelector(getMinTimePrepare)
    const location = useLocation()
    const resSetting = useSelector(getRestaurantSettings)

    const calcCountDownTime = time => setCountDownTime(time - window.appTime)

    const clearCountDown = () =>
        refTimeout.current && clearTimeout(refTimeout.current)

    const getListFoodOrdered = () => {
        return (orderInfo?.data?.foodData || []).map(f => ({
            ...f,
            price: (
                calcPriceSale(f.price, f.saleOffPercent) * f.amount
            ).toFixed(2),
        }))
    }

    const invalidPath = () =>
        location.pathname.indexOf(paths.notFound) !== -1 ||
        location.pathname.indexOf(paths.unSupport) !== -1

    useDidMountEffect(() => {
        if (orderInfo === false) {
            return setCurrentStatus(-3)
        }

        if (!!orderInfo) {
            const { data } = orderInfo
            const { customer, createdDate, timeDeliver } = data;

            if (data.state === 1) {
                let doneDateMoment;

                if (timeDeliver) {
                    doneDateMoment = convertServerTime(timeDeliver);
                } else {
                    doneDateMoment = convertServerTime(createdDate);
                    if (customer?.timeDeliver) {
                        const [hour, minutes] = customer.timeDeliver.split(':');
                        doneDateMoment.set('h', hour);
                        doneDateMoment.set('m', minutes);
                    } else {
                        doneDateMoment.add(minTimePrepare || 0, 'm');
                    }
                }

                calcCountDownTime(doneDateMoment);
            } else {
                clearCountDown();
                setCountDownTime(0);
            }

            if (data.state > currentStatus) {
                if (!data.state) {
                    setCurrentStatus(timeDeliver ? 0 : -1)
                } else {
                    setCurrentStatus(data.state)
                }
            }
        }
    }, [orderInfo])

    useDidMountEffect(() => {
        if (!!statusInfo && !invalidPath()) {
            setCurrentStatus(statusInfo.status)

            if (statusInfo.status === 1 && statusInfo.timeDeliver) {
                calcCountDownTime(convertServerTime(statusInfo.timeDeliver))
            }

            if (statusInfo.status !== 1) {
                clearCountDown();
                setCountDownTime(0);
            }
        }
    }, [statusInfo])

    useDidMountEffect(() => {
        if (countDownTime > 0) {
            const newCountDown = countDownTime - 1000

            refTimeout.current = setTimeout(() => {
                setCountDownTime(newCountDown)
            }, 1000)
        }

        return clearCountDown
    }, [countDownTime])

    useEffect(() => {
        if (invalidPath()) {
            setCurrentStatus(-3)
        }
    }, [location.pathname])

    const statusMap = {
        '-1': {
            title: () => t('modalOrderSuccess.titleSubmitOrderSuccess'),
            message: () => t('modalOrderSuccess.mesSubmitOrderWebSuccess'),
        },
        0: {
            title: () => t('modalOrderSuccess.titleAccepted'),
            message: () =>
                resSetting?.message?.accepted ||
                t('modalOrderSuccess.mesAccepted'),
        },
        1: {
            title: () => t('modalOrderSuccess.titleProcessing'),
            message: () =>
                resSetting?.message?.processing ||
                t('modalOrderSuccess.mesProcessing'),
        },
        2: {
            title: () => t('modalOrderSuccess.titleRestaurantDoneOrder'),
            message: () =>
                orderInfo?.data?.kind === ORDER_METHOD_MAP[ORDER_METHOD.PICKUP]
                    ? resSetting?.message?.pickup_done ||
                      t('modalOrderSuccess.mesDonePickup')
                    : resSetting?.message?.deliver_done ||
                      t('modalOrderSuccess.mesDoneDeliver'),
        },
        '-2': {
            title: () => t('modalOrderSuccess.titleFailed'),
            message: () => t('modalOrderSuccess.mesFailed'),
        },
    }

    const currentStatusData = statusMap[currentStatus]

    return (
        <>
            <audio ref={audioRef} loop>
                <source src={audioBeep} type="audio/mpeg" />
            </audio>
            <ModalForm
                rootClass="modal-order-success"
                isOpen={!!currentStatusData}
                centered
            >
                <DynamicColor
                    style={{ color: COLOR_NAME.BODY_TEXT }}
                    className="modal-order-success__content"
                >
                    {currentStatus !== 2 && (
                        <div className="order-status-hint mb-2">
                            {t('modalOrderSuccess.hintDontCloseTab')}
                        </div>
                    )}

                    <div className="order-id text-center">
                        {'#' + orderInfo?.data?.orderStt}
                    </div>
                    {/* <div className="heading-4 text-center">
                        {currentStatusData?.title && currentStatusData?.title()}
                    </div> */}

                    <div
                        className="mt-3 order-message text-center"
                        dangerouslySetInnerHTML={{
                            __html:
                                currentStatusData?.message &&
                                currentStatusData?.message(),
                        }}
                    />

                    {countDownTime > 0 && (
                        <div className="mt-2 text-center heading-2">
                            {formatTimeFromMillisecond(countDownTime)}
                        </div>
                    )}

                    <StatusList
                        className="mt-4"
                        currentStatus={currentStatus}
                        listStatus={[
                            {
                                status: -1,
                                activeImg: activeSuccess,
                                inactiveImg: inactiveSuccess,
                            },
                            {
                                status: 0,
                                activeImg: activeAccept,
                                inactiveImg: inactiveAccept,
                            },
                            {
                                status: 1,
                                activeImg: activeProcessing,
                                inactiveImg: inactiveProcessing,
                            },
                            {
                                status: [2, -2],
                                activeImg: [activeShipping, failedIcon],
                                inactiveImg: inactiveShipping,
                            },
                        ]}
                    />

                    {/* <Button
                        onClick={handleCloseOrderSuccess}
                        className="mt-8"
                        outline
                    >
                        {t('button.viewDetails')}
                    </Button> */}
                    <ListFoodOrdered
                        foods={getListFoodOrdered()}
                        total={(orderInfo?.data?.totalMoney || 0) / 100}
                        shipMoney={orderInfo?.data?.shipMoney}
                        discountAmount={orderInfo?.data?.voucher?.discountAmount}
                        voucherCode={orderInfo?.data?.voucher?.code}
                    />
                </DynamicColor>
            </ModalForm>
        </>
    )
}

export default ModalWebStatus
